'use client';
import { FC } from 'react';
import { EFinanceStateUpdateValue, InterestRate } from '@/types';
import { numberWithCommas, removeCommas } from '@/utils';
import classes from './styles.module.scss';
import { useTranslation, useWindowDimensions } from '@/hook';
import { FinancingResult } from '../../result';
import { useFormContext } from 'react-hook-form';
import TextFieldFormControlled from '@/components/shared/input/text-field/form-controlled';
import WrappedSlider from '@/components/shared/input/slider';
import WrappedSliderFormControlled from '@/components/shared/input/slider/form-controlled';
import Typography from '@/components/shared/typography/typography.component';
import colors from '@/theme/colors.module.scss';

const defaultMonths = 60;
export const TabMonthlyInstallments: FC<{
    min: number;
    max: number;
    canApply?: boolean;
    eventName?: string;
    carName?: string;
    interestRates: InterestRate[];
    tenorCarPrice: number;
    downPayment: number;
    setDownPayment: React.Dispatch<React.SetStateAction<number>>;
    tenorEMI: number;
    setTenorEMI: React.Dispatch<React.SetStateAction<number>>;
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    type: EFinanceStateUpdateValue;
    minDownpayment: number;
    maxDownpayment: number;
}> = ({
    min,
    max,
    interestRates,
    downPayment,
    setDownPayment,
    tenorEMI,
    setTenorEMI,
    isModalOpen,
    setIsModalOpen,
    tenorCarPrice,
    type,
    minDownpayment,
    maxDownpayment,
}) => {
    const { IS_DESKTOP } = useWindowDimensions();
    const { t } = useTranslation();

    const {
        formState: { errors },
        control,
        watch,
    } = useFormContext();

    return (
        <div className={classes.container}>
            <div
                style={{
                    gap: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography variant="subtitle2Medium" color={colors.gray700}>
                    {t('FinanceCalculator.carPrice')}
                </Typography>

                <div>
                    <TextFieldFormControlled
                        control={control}
                        name="originalPrice"
                        leadingText={t('EGP') as string}
                        numeric
                        key={'op'}
                    />
                    <WrappedSliderFormControlled
                        name="originalPrice"
                        aria-label="Car Price"
                        control={control}
                        valueLabelDisplay="on"
                        sliderLabel
                        step={10000}
                        marks
                        min={min}
                        max={max}
                        valueLabelFormat={value => (
                            <Typography variant={'captionMedium'}>
                                {t('EGP') +
                                    ' ' +
                                    numberWithCommas(Math.ceil(value))}
                            </Typography>
                        )}
                    />
                </div>
            </div>
            <div style={{ marginBlock: '16px' }} />
            <div
                style={{
                    gap: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography
                        variant="subtitle2Medium"
                        className={'textStyle'}
                    >
                        {t('FinanceCalculator.downPayment')}
                    </Typography>
                    <Typography
                        variant="subtitle2Medium"
                        className={'textStyle'}
                    >
                        {!errors?.originalPrice &&
                            `${t('EGP')} ${numberWithCommas(
                                Math.ceil(
                                    (downPayment / 100) *
                                        removeCommas(watch('originalPrice'))
                                )
                            )}`}
                    </Typography>
                </div>
                <div>
                    <WrappedSlider
                        aria-label="Downpayment"
                        defaultValue={downPayment}
                        labelAbove
                        value={downPayment}
                        valueLabelDisplay="on"
                        step={5}
                        marks
                        min={minDownpayment}
                        max={maxDownpayment}
                        onChange={(_, value) => {
                            //   fireFinancingEvent();
                            setDownPayment(value as number);
                        }}
                        valueLabelFormat={value => (
                            <Typography variant={'captionMedium'}>
                                {value + '%'}
                            </Typography>
                        )}
                    />
                </div>
            </div>
            <div
                style={{
                    gap: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography variant="subtitle2Medium" className={'textWithGap'}>
                    {t('Loan Tenor')}
                </Typography>
                <div>
                    <WrappedSlider
                        aria-label="Months"
                        labelAbove
                        defaultValue={defaultMonths}
                        value={tenorEMI}
                        valueLabelDisplay="on"
                        step={12}
                        marks
                        min={12}
                        max={84}
                        onChange={(_, value) => {
                            //   fireFinancingEvent();
                            setTenorEMI(value as number);
                        }}
                        valueLabelFormat={value => (
                            <Typography variant={'captionMedium'}>
                                {value + t('Months')}
                            </Typography>
                        )}
                        // sx={styles.slider}
                    />
                </div>
            </div>

            {!IS_DESKTOP && (
                <div
                    style={{ borderTop: '1px solid #D0D5DD', width: '100%' }}
                />
            )}
            {!IS_DESKTOP && (
                <FinancingResult
                    min={min}
                    max={max}
                    interestRates={interestRates}
                    downPayment={downPayment}
                    tenorEMI={tenorEMI}
                    tenorCarPrice={tenorCarPrice}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    type={type}
                />
            )}
        </div>
    );
};
